import type { iProduct } from 'types/product'
import type { SubscriptionPeriod, iVariation } from 'types/variation'

export const getSubscriptionLabel = (variant: iProduct['variations'][0]) => {
  return isSubscription(variant) ? variant.pa_ep_frequency?.label : undefined
}

const PERIOD_LABELS = { minute: 'Minute', hour: 'Hour', day: 'Day', week: 'Week', month: 'Month', year: 'Year' } as const

/** 'Every 28 Days' from { _subscription_period: 'day', _subscription_period_interval: '28' } */
export const getSubscriptionPeriodLabel = (subscription?: SubscriptionPeriod) => {
  if (!subscription?._subscription_period) return undefined
  const interval = Number(subscription._subscription_period_interval) || 1
  const period = PERIOD_LABELS[subscription._subscription_period]
  return interval > 1 ? `Every ${interval} ${period}s` : `Every ${period}`
}

/** '/month' for a 28 to 31 day cycle, the short price suffix the product hero shows. */
export const getSubscriptionPriceSuffix = (subscription?: Partial<SubscriptionPeriod>) => {
  const period = subscription?._subscription_period
  if (!period) return ''
  const interval = Number(subscription._subscription_period_interval) || 1
  if ((period === 'day' && interval >= 28 && interval <= 31) || (period === 'week' && interval === 4)) return '/month'
  return interval > 1 ? `/ every ${interval} ${period}s` : `/${period}`
}

/** Main image then gallery, falling back to the first variation that has them. */
export const getProductImages = (product: Pick<iProduct, 'image' | 'gallery' | 'variations'>): string[] => {
  const image = product?.image || product?.variations?.find(v => v.image)?.image
  const gallery = product?.gallery || product?.variations?.find(v => v.gallery)?.gallery || []
  return [image, ...gallery].filter(Boolean)
}

export const isSubscription = (variant: iProduct['variations'][0]) => {
  if (!variant) return false
  if (variant.pa_ep_frequency) return variant.pa_ep_frequency.slug !== 'one-time-purchase'
  // Products with no frequency attribute carry their recurring terms in a
  // subscription block instead (menopause plan: period day, interval 28).
  return !!variant.subscription?._subscription_period
}

const SUBSCRIPTION_PRODUCT_TYPES = ['variable-subscription', 'mps-product']

/** Whether a product is sold on a recurring basis at all. */
export const sellsSubscriptions = (product?: Pick<iProduct, 'type'>) => SUBSCRIPTION_PRODUCT_TYPES.includes(product?.type ?? '')

/**
 * Strips the subscription block from products that aren't sold as recurring.
 * WooCommerce keeps default terms on every variation, so a one-off product
 * arrives carrying a month/1 block that the cart would read as a subscription.
 */
export const withoutStaleSubscription = <T extends Pick<iProduct, 'type' | 'variations'>>(product: T): T => {
  if (!product?.variations?.length || sellsSubscriptions(product)) return product

  return { ...product, variations: product.variations.map(({ subscription: _subscription, ...variant }) => variant) }
}

// Goes straight to checkout with no treatment card. An mps-product without variations has nothing
// to add, so it keeps the card instead.
export const skipsTreatmentSelection = (product: Pick<iProduct, 'type' | 'variations'>) => {
  return product.type === 'pf-product' || (product.type === 'mps-product' && !!product.variations?.length)
}

/** A cart item sent with a parent product hash, and the variation to use instead (null when none fits). */
export type ParentHashResolution = { hash: string; variant: iVariation | null }

// A parent hash doesn't say which variation was meant, so only a single in-stock one-off is picked:
// a patient is never signed up to a subscription they didn't choose. Null when `hash` isn't a parent with variations.
export const resolveParentHash = (hash: string, product: Pick<iProduct, 'hash' | 'variations'> | null): ParentHashResolution | null => {
  if (!product || product.hash !== hash || !product.variations?.length) return null
  const oneOff = product.variations.filter(variant => variant.in_stock && !variant.internal_only && !isSubscription(variant))
  return { hash, variant: oneOff.length === 1 ? oneOff[0] : null }
}
